import type { ElementRef, OnInit } from '@angular/core';
import {
    Component,
    inject,
    input,
    computed,
    ChangeDetectionStrategy,
    DestroyRef,
    ViewChild,
    signal,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import gsap from 'gsap';

import type { Observable } from 'rxjs';

import { ESelectedEntityType } from '@common/types/layout.type';

import { themeColorCss } from '@sites/site-theme/src/utils/variables';

import { getGsapAnimation } from '@components/sp-animation-settings/sp-animation-settings.gsap-getters';

import type { TabForm } from '@sites/entity-signal-form';

import type { DenormalizedTabEntity } from '@web/types';

import { LayoutFacade } from '@web/store/layout/layout.facade';

import { WebTabFormService } from '@web/core/services/web-tab-form.service';

import { DefaultGridComponent } from '@web/layout/content/page-content/components/grids';
import { TabControlsComponent } from '@web/layout/content/page-content/components/controls';

import { TabHeaderWidthComponent } from '../tab-header-width/tab-header-width.component';
import type { CornersLike } from '@sites/entity-core';
import {
    colorValueVar,
    cornersShorthandVar,
    cornerVar,
    numberWithUnitVar,
    spacingShorthandVar,
} from '@sites/entity-core';

@Component({
    selector: 'web-default-tab',
    templateUrl: './default-tab.component.html',
    styleUrls: ['./default-tab.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, TabHeaderWidthComponent, DefaultGridComponent, TabControlsComponent],
})
export class DefaultTabComponent implements OnInit {
    public readonly entity = input.required<DenormalizedTabEntity>();

    private readonly layoutFacade = inject(LayoutFacade);
    private readonly tabFormService = inject(WebTabFormService);
    private readonly destroyRef = inject(DestroyRef);

    @ViewChild('animatedElem') public animatedElem: ElementRef<HTMLDivElement>;

    public form!: TabForm;

    public readonly hideControls$: Observable<boolean> = this.layoutFacade.hideControls$;
    public readonly entityId$: Observable<string> = this.layoutFacade.entityId$;

    private readonly childrenContentMap = computed(() => {
        const map = new Map<string, DenormalizedTabEntity['children'][number]>();
        (this.entity().children ?? []).forEach((child) => {
            map.set(child.id, child);
        });
        return map;
    });

    public readonly children = computed(() => this.entity().children ?? []);

    public readonly activeIndex = computed(() => {
        const idx = this.form
            .tabs()
            .value()
            .findIndex((tab) => tab?.isActive);
        return idx !== -1 ? idx : 0;
    });

    public readonly activeChild = computed(() => {
        const activeTab = this.form.tabs().value()[this.activeIndex()];
        if (!activeTab) {
            return null;
        }
        return this.childrenContentMap().get(activeTab.contentEntityId);
    });

    public readonly rootElementId = computed(() => `tab_root_${this.entity().id}`);
    public readonly headerElementId = computed(() => `tab_header_${this.entity().id}`);
    public readonly bodyElementId = computed(() => `tab_body_${this.entity().id}`);

    public readonly tabHeaderBg = computed(() =>
        colorValueVar(this.form.headerBackgroundColor().value(), themeColorCss)
    );
    public readonly tabHeaderActiveBg = computed(() =>
        colorValueVar(this.form.headerActiveBackgroundColor().value(), themeColorCss)
    );
    public readonly tabHeaderText = computed(() => colorValueVar(this.form.headerTextColor().value(), themeColorCss));
    public readonly tabHeaderActiveText = computed(() =>
        colorValueVar(this.form.headerActiveTextColor().value(), themeColorCss)
    );
    public readonly tabHeaderActiveBorder = computed(() =>
        colorValueVar(this.form.headerActiveContourColor().value(), themeColorCss)
    );
    public readonly tabHeaderWidth = computed(() => {
        const w = this.form.headerWidth().value();
        return numberWithUnitVar({ value: w?.value ?? 100, unit: w?.unit ?? 'px' });
    });
    public readonly tabHeaderFontSize = computed(() => numberWithUnitVar(this.form.headerFontSize().value()) ?? '');
    public readonly tabHeaderFontWeight = computed(() => this.form.headerFontWeight().value() ?? '');
    public readonly tabHeaderFontFamily = signal('var(--p-font-family)');
    public readonly tabHeaderFontStyle = computed(() => this.form.headerFontStyle().value() ?? '');
    public readonly tabHeaderTextTransform = computed(() => this.form.headerTextTransform().value() ?? '');
    public readonly tabHeaderPadding = computed(() => spacingShorthandVar(this.form.headerPadding().value()) ?? '');
    public readonly tabHeaderRadius = computed(() => cornersShorthandVar(this.form.headerBorderRadius().value()));
    public readonly tabHeaderRadiusMobile = computed(() =>
        this.form.headerBorderRadius().value()
            ? cornerVar(this.form.headerBorderRadius().value(), 'topLeft')
            : undefined
    );
    public readonly tabSeparatorColor = computed(() =>
        colorValueVar(this.form.headerSeparatorColor().value(), themeColorCss)
    );
    public readonly tabSeparatorBorder = computed(() => {
        const color = this.tabSeparatorColor();
        return this.form.headerSeparator().value() ? `1px solid ${color}` : '0';
    });
    public readonly tabActiveBorders = computed(() => {
        const color = this.tabHeaderActiveBorder();
        const side = this.form.headerActiveContourSide().value();
        const paddingValue = this.form.headerPaddingValue().value();
        const width = color === 'transparent' ? '0' : `${this.getActiveBorderSize(paddingValue?.top)}px`;
        return {
            top: side === 'top' ? `${width} solid ${color}` : '0',
            right: side === 'right' ? `${width} solid ${color}` : '0',
            bottom: side === 'bottom' ? `${width} solid ${color}` : '0',
            left: side === 'left' ? `${width} solid ${color}` : '0',
        };
    });
    public readonly tabBodyBg = computed(() => colorValueVar(this.form.bodyBackgroundColor().value(), themeColorCss));
    public readonly tabBodyPadding = computed(() => spacingShorthandVar(this.form.bodyPadding().value()));
    public readonly tabBodyRadius = computed(() =>
        cornersShorthandVar(this.preprocessBodyBorderRadius(this.form.bodyBorderRadius().value()))
    );

    public ngOnInit(): void {
        this.form = this.tabFormService.getOrCreateScoped(this.entity().id, this.destroyRef);
    }

    // Methods
    public setActive(index: number): void {
        if (this.activeIndex() === index) return;

        this.form.updateTabs((tabs) =>
            tabs.map((item, i) => {
                if (index === i) {
                    return {
                        ...item,
                        isActive: true,
                    };
                }

                return {
                    ...item,
                    isActive: false,
                };
            })
        );

        const animation = this.form.animation().value();
        if (animation?.isActive && this.animatedElem) {
            const el = this.animatedElem.nativeElement;
            gsap.killTweensOf(el);
            gsap.set(el, { clearProps: 'opacity,transform' });
            getGsapAnimation(animation as any, el);
        }
    }

    public selectEntity(): void {
        this.layoutFacade.setSelectedEntity({ entityType: ESelectedEntityType.tab, entityId: this.entity().id });
    }

    public deleteEntity(): void {
        // this.tabsFacade.remove(this.entity());
        this.form.remove();
    }

    public isTabContentEmpty(contentEntityId: string): boolean {
        const grid = this.childrenContentMap().get(contentEntityId);
        if (!grid) {
            return true;
        }

        return grid.children.every((inner) => inner.length === 0);
    }

    public handleHeaderWidthDragStart(): void {
        this.form.beginTransaction();
    }

    public handleHeaderWidthChange(nextValue: number): void {
        this.form.setHeaderWidth(nextValue);
    }

    public handleHeaderWidthDragEnd(): void {
        this.form.commitTransaction();
    }

    private preprocessBodyBorderRadius(borderRadius?: CornersLike): CornersLike | undefined {
        if (!borderRadius) {
            return undefined;
        }

        const { value, unit } = borderRadius;
        if (this.activeIndex() !== 0) {
            return {
                value,
                unit,
            };
        }

        if (this.form.placement().value() === 'left') {
            return {
                value: {
                    ...value,
                    topLeft: 0,
                },
                unit,
            };
        }

        if (this.form.placement().value() === 'right') {
            return {
                value: {
                    ...value,
                    topRight: 0,
                },
                unit,
            };
        }

        return {
            value,
            unit,
        };
    }

    // TODO: These methods should be moved to a shared utility file since they're used in multiple places

    private getActiveBorderSize(headerTopPadding: number): number | undefined {
        if (!headerTopPadding) {
            return undefined;
        }

        const map = {
            8: 2,
            16: 6,
        };

        return map[headerTopPadding] ?? 4;
    }
}
